import React, { useState } from 'react';
import { copyText } from '../../utils/clipboard';

/**
 * Calling a declared endpoint, from the browser.
 *
 * From the BROWSER on purpose. The obvious alternative is to POST the request to
 * our backend and have it make the call, which would dodge CORS — and would also
 * turn this page into an SSRF hole with a UI: any developer could aim it at
 * anything our network can reach and read the response. The scheduled prober has
 * to be server-side and is guarded accordingly (app/utils/egress.py); this does
 * not have to be, so it isn't. The request goes out with the developer's own
 * network and the developer's own credential, and the platform holds neither.
 *
 * The cost is CORS, which will block a fair number of hosts. That is worth
 * saying out loud rather than rendering as a generic failure — an app whose API
 * refuses a browser is a real fact about the app, not a bug in this page.
 */

const METHOD_CLASS = {
  GET: 'get', HEAD: 'get', POST: 'post', PUT: 'put', PATCH: 'put', DELETE: 'del', OPTIONS: 'meta',
};

export function methodClass(m) {
  return METHOD_CLASS[String(m || 'GET').toUpperCase()] || 'meta';
}

/** Everything at this path, using the same small subset the CLI supports. */
function pathPresent(body, path) {
  let node;
  try { node = JSON.parse(body); } catch { return false; }
  for (const part of String(path).replace(/^\$/, '').split('.')) {
    if (!part) continue;
    const [, key, index] = part.match(/^([^[]*)(?:\[(.+)\])?$/) || [];
    if (key) {
      if (node === null || typeof node !== 'object' || !(key in node)) return false;
      node = node[key];
    }
    if (index !== undefined) {
      if (index === '*') {
        if (!Array.isArray(node) || node.length === 0) return false;
        node = node[0];
      } else {
        const n = Number(index);
        if (!Array.isArray(node) || !node[n]) return false;
        node = node[n];
      }
    }
  }
  return true;
}

/** Did the response match what the endpoint said to expect? */
export function grade(expect, status, body) {
  if (!expect || typeof expect !== 'object') return null;
  const failures = [];
  if (expect.status != null && status !== expect.status) {
    failures.push(`expected ${expect.status}, got ${status}`);
  }
  if (expect.json && !pathPresent(body, expect.json)) {
    failures.push(`${expect.json} not found in the response`);
  }
  return { ok: failures.length === 0, failures };
}

/**
 * Why this endpoint cannot be sent, or null.
 *
 * An endpoint with no URL used to be sendable, and `fetch('')` resolves against
 * the current page — so Send called the developer portal, got its index.html
 * back with a 200, and graded that against `expect: {status: 200}` as a pass.
 * A green tick on a request that never reached the app is worse than any error
 * this could have shown instead.
 */
export function cannotSend(endpoint) {
  const raw = (endpoint?.url || '').trim();
  if (!raw) {
    return 'This endpoint has no URL yet. Add an absolute one — without it, Send would call '
      + 'this portal rather than your app.';
  }
  let parsed;
  try {
    parsed = new URL(raw);
  } catch {
    return `“${raw}” is not an absolute URL. It needs a scheme and a host, like `
      + 'https://your.app/healthz — a relative path resolves against this portal, not your app.';
  }
  if (!['http:', 'https:'].includes(parsed.protocol)) {
    return `${parsed.protocol} is not a scheme a browser will call here — use http or https.`;
  }
  return null;
}

function buildUrl(endpoint, values) {
  let url = endpoint.url || '';
  const query = [];
  for (const p of (endpoint.params || [])) {
    const v = values[p.name];
    if (v === undefined || v === '') continue;
    if (p.in === 'path') url = url.replace(`{${p.name}}`, encodeURIComponent(v));
    else query.push(`${encodeURIComponent(p.name)}=${encodeURIComponent(v)}`);
  }
  return query.length ? `${url}${url.includes('?') ? '&' : '?'}${query.join('&')}` : url;
}

function authHeaders(endpoint, schemes, secret) {
  const id = endpoint.auth;
  if (!id || id === 'none' || !secret) return {};
  const spec = (schemes || {})[id] || (typeof id === 'object' ? id : {});
  if (spec.type === 'bearer') return { Authorization: `Bearer ${secret}` };
  if (spec.type === 'basic') return { Authorization: `Basic ${secret}` };
  if ((spec.location || 'header') === 'header') return { [spec.name || 'X-API-Key']: secret };
  return {};
}

export function asCurl(endpoint, schemes, values, secret) {
  const url = buildUrl(endpoint, values);
  const method = String(endpoint.method || 'GET').toUpperCase();
  const headers = authHeaders(endpoint, schemes, secret ? '$TOKEN' : '');
  const parts = [`curl -X ${method} '${url}'`];
  for (const [k, v] of Object.entries(headers)) parts.push(`  -H '${k}: ${v}'`);
  return parts.join(' \\\n');
}

export default function CapabilityRunner({ endpoint, schemes, onResult }) {
  const [values, setValues] = useState({});
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState('');

  const needsAuth = endpoint.auth && endpoint.auth !== 'none';
  const method = String(endpoint.method || 'GET').toUpperCase();
  const blocked = cannotSend(endpoint);

  const send = async () => {
    if (blocked) return;
    setBusy(true);
    setResult(null);
    const url = buildUrl(endpoint, values);
    const started = performance.now();
    try {
      const res = await fetch(url, {
        method,
        headers: authHeaders(endpoint, schemes, secret),
      });
      const body = await res.text();
      const ms = Math.round(performance.now() - started);
      const outcome = {
        status: res.status, ok: res.ok, ms, body,
        type: res.headers.get('content-type') || '',
        graded: grade(endpoint.expect, res.status, body),
        at: Date.now(),
      };
      setResult(outcome);
      onResult?.(outcome);
    } catch (err) {
      // A cross-origin refusal is indistinguishable from a network error to
      // fetch(), so say what it probably is instead of guessing wrong.
      const outcome = {
        blocked: true,
        message: `The browser could not reach ${url}. This is usually CORS — the app has to send `
          + `Access-Control-Allow-Origin for this page to call it. Run \`paradigm capabilities test\` `
          + `from your machine to check the endpoint itself, which is not subject to CORS.`,
        raw: String(err?.message || err),
        at: Date.now(),
      };
      setResult(outcome);
      onResult?.(outcome);
    }
    setBusy(false);
  };

  // `copied` names what landed on the clipboard, or 'failed' — never a guess.
  const copy = async (what, text) => {
    setCopied((await copyText(text)) ? what : 'failed');
    setTimeout(() => setCopied(''), 1600);
  };

  const pretty = (body) => {
    try { return JSON.stringify(JSON.parse(body), null, 2); } catch { return body; }
  };

  return (
    <div className="cap-run">
      {(endpoint.params || []).length > 0 && (
        <div className="cap-run__grid">
          {(endpoint.params || []).map(p => (
            <React.Fragment key={p.name}>
              <label htmlFor={`p-${p.name}`}>
                {p.name}
                {p.required && <em title="required">*</em>}
              </label>
              <input
                id={`p-${p.name}`}
                value={values[p.name] || ''}
                placeholder={p.type || 'string'}
                onChange={e => setValues(v => ({ ...v, [p.name]: e.target.value }))}
              />
            </React.Fragment>
          ))}
        </div>
      )}

      {needsAuth && (
        <div className="cap-run__grid">
          <label htmlFor="secret">{String(endpoint.auth)}</label>
          <input
            id="secret"
            type="password"
            value={secret}
            placeholder="the credential for this scheme"
            onChange={e => setSecret(e.target.value)}
          />
          <span />
          {/* It never leaves the tab: the request is made by this browser, and
              nothing here is sent to Paradigm or written down. */}
          <span className="cap-run__hint">
            Stays in this tab — the request is sent by your browser, not by ofSelf,
            and this is never stored.
          </span>
        </div>
      )}

      {blocked && <p className="cap-run__blocked">{blocked}</p>}

      <div className="cap-run__actions">
        <button className="cap-btn" onClick={send} disabled={busy || !!blocked}
                title={blocked || undefined}>
          {busy ? 'Sending…' : `Send ${method}`}
        </button>
        <button
          className="cap-btn cap-btn--ghost"
          onClick={() => copy('curl', asCurl(endpoint, schemes, values, secret))}
        >
          {copied === 'curl' ? 'Copied' : copied === 'failed' ? 'Could not copy' : 'Copy as curl'}
        </button>
        {endpoint.expect && (
          <span className="cap-run__expect">
            expects {endpoint.expect.status && <code>{endpoint.expect.status}</code>}
            {endpoint.expect.status && endpoint.expect.json && ' and '}
            {endpoint.expect.json && <code>{endpoint.expect.json}</code>}
          </span>
        )}
      </div>

      {result?.blocked && (
        <div className="cap-resp cap-resp--blocked">
          <p>{result.message}</p>
          <code>{result.raw}</code>
        </div>
      )}

      {result && !result.blocked && (
        <div className="cap-resp">
          <div className="cap-resp__bar">
            <span className={`cap-resp__code ${result.ok ? 'is-ok' : 'is-down'}`}>{result.status}</span>
            <span>{result.ms} ms</span>
            <span>{(result.body.length / 1024).toFixed(1)} KB</span>
            <span className="cap-resp__type">{result.type.split(';')[0]}</span>
            {result.graded && (
              <span className={`cap-resp__grade ${result.graded.ok ? 'is-ok' : 'is-down'}`}>
                {result.graded.ok ? '✓ matches expect' : `✕ ${result.graded.failures.join('; ')}`}
              </span>
            )}
          </div>
          <pre>{pretty(result.body).slice(0, 20000) || '(empty body)'}</pre>
        </div>
      )}
    </div>
  );
}
